import { Eye } from "lucide-react";
import { Deployment } from "../../../../types";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";

interface DeploymentDetailsModalProps {
  deployment: Deployment | null;
  logs: Record<string, string[]>;
  onClose: () => void;
  onLoadLogs: (deploymentId: string) => void;
}

export function DeploymentDetailsModal({
  deployment,
  logs,
  onClose,
  onLoadLogs,
}: DeploymentDetailsModalProps) {
  if (!deployment) return null;

  const deploymentLogs = logs[deployment.id] || [];

  return (
    <Dialog open={!!deployment} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                deployment.status === "running"
                  ? "bg-success"
                  : deployment.status === "failed"
                    ? "bg-destructive"
                    : deployment.status === "building" ||
                        deployment.status === "queued"
                      ? "bg-warning animate-pulse"
                      : "bg-muted-foreground"
              }`}
            />
            <span>Deployment Details</span>
          </DialogTitle>
          <DialogDescription>
            {deployment.name || `Version ${deployment.version || "latest"}`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Deployment Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="mb-2 text-h5">Status</h4>
              <Badge
                variant={
                  deployment.status === "running"
                    ? "default"
                    : deployment.status === "failed"
                      ? "destructive"
                      : "secondary"
                }
              >
                {deployment.status}
              </Badge>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Created</h4>
              <p className="text-sm text-muted-foreground">
                {new Date(deployment.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Environment</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.environment}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Branch</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.branch}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Framework</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.framework}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Port</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.port || "N/A"}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Service ID</h4>
              <p className="text-sm text-muted-foreground font-mono">
                {deployment.serviceId}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Deployment ID</h4>
              <p className="text-sm text-muted-foreground font-mono">
                {deployment.id}
              </p>
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="mb-2 text-h5">Version</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.version || "latest"}
              </p>
            </div>
            <div>
              <h4 className="mb-2 text-h5">Last Updated</h4>
              <p className="text-sm text-muted-foreground">
                {deployment.updatedAt
                  ? new Date(deployment.updatedAt).toLocaleString()
                  : "N/A"}
              </p>
            </div>
            {deployment.url && (
              <div>
                <h4 className="mb-2 text-h5">URL</h4>
                <p className="text-sm text-muted-foreground">
                  <a
                    href={deployment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {deployment.url}
                  </a>
                </p>
              </div>
            )}
            {deployment.dockerImage && (
              <div>
                <h4 className="mb-2 text-h5">Docker Image</h4>
                <p className="text-sm text-muted-foreground font-mono">
                  {deployment.dockerImage}
                </p>
              </div>
            )}
          </div>

          {/* Failure Reason */}
          {deployment.status === "failed" && (
            <div className="p-4 bg-destructive/10 border-destructive/30 rounded-lg">
              <h4 className="mb-2 text-h5 text-destructive">
                Deployment Failed
              </h4>
              <p className="text-sm text-destructive">
                {deployment.failureReason ||
                  "No specific failure reason available. Check logs for more details."}
              </p>
            </div>
          )}

          {/* Deployment Logs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-h5">Deployment Logs</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onLoadLogs(deployment.id)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {deploymentLogs.length ? "Refresh Logs" : "Load Logs"}
              </Button>
            </div>
            <div className="bg-terminal text-terminal-foreground p-4 rounded-lg text-xs font-mono max-h-60 overflow-y-auto">
              {deploymentLogs.length > 0 ? (
                deploymentLogs.map((log, index) => (
                  <div key={index} className="mb-1">
                    {log}
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground">
                  No logs available for this deployment. Click "Load Logs" to
                  fetch them.
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
